import { ProfileCircle } from "iconsax-reactjs";
import React from "react";

type ClassValue = string | undefined | null | false;
function cn(...classes: ClassValue[]) {
  return classes.filter(Boolean).join(" ");
}

export function Chip({
  children,
  className,
  ...props
}: {
  children: React.ReactNode;
  className?: string;
} & React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      {...props}
      className={cn(
        "inline-flex items-center rounded-full bg-[#EFF0FA] px-3 py-0.5 text-[14px] font-[350] text-[#2C2F44]",
        className,
      )}
    >
      {children}
    </span>
  );
}

export function Avatar({
  name,
  email,
  size = 40,
  showStatusDot = false,
  managedBusinessesCount = 0,
  icon,
}: {
  name: string;
  email?: string;
  size?: number;
  showStatusDot?: boolean;
  managedBusinessesCount?: number;
  icon?: React.ReactNode;
}) {
  const initials = React.useMemo(() => {
    const parts = name.trim().split(/\s+/);
    const a = parts[0]?.[0] ?? "";
    const b = parts[1]?.[0] ?? "";
    const fallback = (email?.[0] ?? "").toUpperCase();
    return (a + b).trim().toUpperCase() || fallback || "?";
  }, [name, email]);

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <div
        className="grid place-items-center rounded-full bg-[#EEF1FB] text-[14px] font-semibold text-[#2C2F44]"
        style={{ width: size, height: size }}
      >
        {icon ? icon : initials}
      </div>

      {showStatusDot && managedBusinessesCount > 0 && (
        <span className="absolute bottom-0 right-0 grid h-4 w-4 place-items-center">
          <ProfileCircle size={12} color="#E93A0D" />
        </span>
      )}
    </div>
  );
}

export type DataTableColumn<T> = {
  id: string;
  header: React.ReactNode;
  headerClassName?: string;
  cellClassName?: string;
  cell: (row: T, rowIndex: number) => React.ReactNode;
};

export type DataTableProps<T> = {
  columns: Array<DataTableColumn<T>>;
  data: Array<T>;
  getRowKey?: (row: T, index: number) => string;
  className?: string;
  tableClassName?: string;
  rowClassName?: string | ((row: T, index: number) => string);
  onRowClick?: (row: T) => void;
  emptyState?: React.ReactNode;
};

export function DataTable<T>({
  columns,
  data,
  getRowKey,
  className,
  tableClassName,
  rowClassName,
  onRowClick,
  emptyState,
}: DataTableProps<T>) {
  const clickable = typeof onRowClick === "function";

  return (
    <div
      className={cn(
        "overflow-hidden rounded-[16px] border-y border-[#EEF1FB] bg-white",
        className,
      )}
    >
      <div className="w-full overflow-x-auto">
        <table
          className={cn("w-full border-collapse text-left", tableClassName)}
        >
          <thead className="bg-[#FBFCFE]">
            <tr className="table-row border-b border-[#EEF1FB]">
              {columns.map((col) => (
                <th
                  key={col.id}
                  className={cn(
                    "table-cell px-4 py-3 whitespace-nowrap text-left tracking-[-0.36px] align-middle text-[12px] font-medium text-[#475467]",
                    col.headerClassName,
                  )}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="text-[13px] text-[#344054]">
            {data.length === 0 ? (
              <tr className="table-row">
                <td className="table-cell px-4 py-10" colSpan={columns.length}>
                  {emptyState ?? (
                    <div className="text-[#6B6F8A]">No records found.</div>
                  )}
                </td>
              </tr>
            ) : (
              data.map((row, rowIndex) => {
                const key = getRowKey
                  ? getRowKey(row, rowIndex)
                  : String(rowIndex);

                return (
                  <tr
                    key={key}
                    className={cn(
                      "group table-row border-b border-[#EEF1FB] last:border-b-0 transition-colors",
                      clickable && "cursor-pointer hover:bg-[#FAFBFF]",
                      typeof rowClassName === "function"
                        ? rowClassName(row, rowIndex)
                        : rowClassName,
                    )}
                    onClick={() => clickable && onRowClick?.(row)}
                  >
                    {columns.map((col) => (
                      <td
                        key={col.id}
                        className={cn(
                          "table-cell px-4 py-3 align-middle",
                          col.cellClassName,
                        )}
                      >
                        {col.cell(row, rowIndex)}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function CompactChips({
  items,
  maxVisible = 1,
}: {
  items: string[];
  maxVisible?: number;
}) {
  const visible = items.slice(0, maxVisible);
  const remaining = Math.max(items.length - visible.length, 0);

  return (
    <div className="flex flex-wrap items-center gap-2">
      {visible.map((t) => (
        <Chip key={t}>{t}</Chip>
      ))}
      {remaining > 0 && <Chip>+{remaining}</Chip>}
    </div>
  );
}
